import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';
import { FormDateInput, FormSelect, FormSwitch, FormTextArea, FormTextInput } from 'components/form-fields';
import { useValidation } from 'hooks/useValidation';
import { ShowHide } from 'components/layout';
import { NotificationInline } from 'components/notifications';
import { getDefaultSystemMessageDates } from './systemMessageModel';

const earliestSelectableDate = moment("2000-01-01").toDate();
const storedDateFormat = "YYYY-MM-DD HH:mm";

// Dates are held as UTC strings, so the comparison has to be made in UTC as well - reading one as a
// local date lands an hour out through British Summer Time.
function isAfterNow(storedDate) {
    if (storedDate == null) {
        return false;
    }

    return moment.utc(storedDate, storedDateFormat).isAfter(moment.utc());
}

export function SystemMessageForm(
    {
        saving = false,
        loading = true,
        isSystemMessageDirty = false,
        disabled = false,
        isActiveEditable = true,
        isUpdatingSystemMessage = true,
        onSystemMessageUpdate,
        onValidationUpdate,
        originalSystemMessage,
        systemMessage,
        systemMessageTypes
    }
) {
    const noSystemMessageTypeId = 0;
    const maintenanceSystemMessageTypeId = 1;
    const incidentSystemMessageTypeId = 2;
    const generalSystemMessageTypeId = 3;
    const fiveMinuteAllowance = 5;
    const systemMessageIdsWithNoDateField = [noSystemMessageTypeId];
    const isIncident = systemMessage.systemMessageTypeID === incidentSystemMessageTypeId;
    const isScheduledAhead = systemMessage.systemMessageTypeID === maintenanceSystemMessageTypeId
        || systemMessage.systemMessageTypeID === generalSystemMessageTypeId;
    const todayAllowance = systemMessage.systemMessageTypeID === maintenanceSystemMessageTypeId
        ? fiveMinuteAllowance
        : 0;
    const wasIncident = originalSystemMessage.systemMessageTypeID === incidentSystemMessageTypeId;
    const [systemMessageValid, errors, validateSystemMessage] = useValidation();
    const [toolTipText, setToolTipText] = useState("");
    const [hasBeenEdited, setHasBeenEdited] = useState(false);

    // This is a workaround to make sure the minDate for StartDate is only set when
    // proto.systemMessage.startDate is set. This is because proto.systemMessage.startDate
    // is set asyncronously and the minDate is set before the value is set.
    // resulting in an out of range date error.
    const getMinStartDate = useCallback(() => {
        // An incident is recorded after the event, so its start date has to reach back into the past.
        if (systemMessage.systemMessageTypeID === incidentSystemMessageTypeId) {
            return earliestSelectableDate;
        }

        // The floor is the whole minute now running rather than the moment within it. The time controls
        // step in whole minutes, so a floor still carrying the seconds elapsed in the current minute
        // would put that minute out of reach and hold the author to the next one.
        return systemMessage.startDate
            && moment.utc(systemMessage.startDate).toDate() < moment().startOf('minute').toDate()
            ? moment.utc(systemMessage.startDate).toDate()
            : moment().startOf('minute').toDate();
    }, [systemMessage.startDate, systemMessage.systemMessageTypeID]);

    const [minStartDate, setMinStartDate] = useState(getMinStartDate);

    useEffect(() => {
        setMinStartDate(getMinStartDate());
    }, [systemMessage.startDate, getMinStartDate]);

    // A message cannot end before it started, and can end at the moment it started.
    function getMinEndDate() {
        if (!systemMessage.startDate) {
            return earliestSelectableDate;
        }

        return moment.utc(systemMessage.startDate).toDate();
    }

    function getMaxStartDate() {
        const endDate = systemMessage.endDate ? moment.utc(systemMessage.endDate) : null;

        // An incident records something that has already happened, so it can start neither after it
        // ended nor in the future - whichever of the two comes first is the cap.
        if (isIncident) {
            if (endDate == null) {
                return moment().toDate();
            }

            return moment.min(endDate, moment()).toDate();
        }

        if (endDate == null) {
            return null;
        }

        return endDate.toDate();
    }

    function getMaxEndDate() {
        // An incident is closed once it is over, so it cannot end in the future either.
        if (isIncident) {
            return moment().toDate();
        }

        return null;
    }

    // A maintenance window that has already begun cannot have its start moved. The saved message is
    // what settles that, not the value on screen, which passes through half-typed readings of a date
    // while the author fills the field in. The type has to be maintenance on both sides as well, or a
    // message stored as another type with a start in the past would lock the date it has just asked
    // the author for.
    function isStartDateLocked() {
        return isUpdatingSystemMessage
            && systemMessage.systemMessageTypeID === maintenanceSystemMessageTypeId
            && originalSystemMessage.systemMessageTypeID === maintenanceSystemMessageTypeId
            && moment.utc(originalSystemMessage.startDate).toDate() < moment().toDate();
    }

    // Records that the author has changed something, in step with the change itself, so the very
    // first change is validated. The dirty-data flag settles a render later, which is too late.
    function updateSystemMessage(updatedSystemMessage) {
        setHasBeenEdited(true);
        onSystemMessageUpdate(updatedSystemMessage);
    }

    // A day chosen from the calendar carries no time of its own, so it arrives at local midnight, which
    // on today's date is behind the clock. A scheduled message is stamped with the current time instead,
    // plus an allowance on maintenance to clear the floor the API holds those windows to. Midnight on any
    // other day is a time they have asked for and is kept. The comparison is local, matching the pickers,
    // while the value the form holds stays UTC.
    function toStoredDate(selectedDate) {
        const selected = moment(selectedDate);

        if (isScheduledAhead
            && selected.isSame(moment(), 'day')
            && selected.isSame(selected.clone().startOf('day'))) {
            return moment().add(todayAllowance, 'm').utc().format(storedDateFormat);
        }

        return selected.utc().format(storedDateFormat);
    }

    function onStartDateChange(event) {
        updateSystemMessage({
            ...systemMessage,
            startDate: event ? toStoredDate(event) : null
        });
    }

    function onEndDateChange(event) {
        updateSystemMessage({
            ...systemMessage,
            endDate: event ? toStoredDate(event) : null
        });
    }

    // The type can only be chosen while the message is being created, so this runs on a message that
    // has no saved dates to preserve.
    function onMessageTypeChange(item, originalValue) {
        if (originalValue === null || item.id === systemMessage.systemMessageTypeID) {
            return;
        }

        // Each type reads its dates differently - an incident's record when it happened, a banner's
        // set when it shows - so the dates come from the type now chosen rather than carrying over.
        updateSystemMessage({
            ...systemMessage,
            systemMessageTypeID: item.id,
            ...getDefaultSystemMessageDates(item.id)
        });
    }

    function onMessageChange(event) {
        updateSystemMessage({
            ...systemMessage,
            message: event.target.value
        });
    }

    function onUrlChange(event) {
        updateSystemMessage({
            ...systemMessage,
            url: event.target.value
        });
    }

    function onActiveChange(event) {
        updateSystemMessage({
            ...systemMessage,
            active: event.target.checked
        });
    }

    useEffect(onValidationChange, [systemMessageValid]);

    function onValidationChange() {
        onValidationUpdate(systemMessageValid)
    }

    useEffect(validate, [systemMessage]);

    function validate() {
        if (isSystemMessageDirty || hasBeenEdited) {
            let isEndDateRequired = !systemMessageIdsWithNoDateField.includes(systemMessage.systemMessageTypeID);

            if (isIncident) {
                // An incident only needs an end date to close it, and an end date already stored
                // against an incident cannot be taken away again to reopen it. The stored type has to
                // match as well, or a message converted into an incident would inherit the rule from
                // the dates it arrived with.
                isEndDateRequired = isUpdatingSystemMessage && wasIncident && originalSystemMessage.endDate != null;
            }

            let rules = [
                { fieldName: "startDate", required: !systemMessageIdsWithNoDateField.includes(systemMessage.systemMessageTypeID) },
                { fieldName: "endDate", required: isEndDateRequired },
                { fieldName: "systemMessageTypeID", required: true },
                { fieldName: "message", required: true }
            ];

            let futureDateRules = null;

            // The pickers hold the author to whole days, so a later time today is caught here instead.
            if (isIncident) {
                futureDateRules = [
                    {
                        fieldName: "startDate",
                        valid: !isAfterNow(systemMessage.startDate),
                        errorMessage: "An incident cannot start in the future"
                    },
                    {
                        fieldName: "endDate",
                        valid: !isAfterNow(systemMessage.endDate),
                        errorMessage: "An incident cannot end in the future"
                    }
                ];
            }

            validateSystemMessage(rules, systemMessage, futureDateRules);
        }
    }

    useEffect(() => {
        if (systemMessage.systemMessageTypeID === maintenanceSystemMessageTypeId) {
            setToolTipText("For maintenance messages the start and end dates refer to the time the release is expected to take place");
        }
        else if (systemMessage.systemMessageTypeID === generalSystemMessageTypeId) {
            setToolTipText("For general messages the start and end dates refer to the time you would like the banner to appear on Vision");
        }
        else if (systemMessage.systemMessageTypeID === incidentSystemMessageTypeId) {
            setToolTipText("For incidents the start date records when the incident began, and setting an end date closes it.");
        }

    }, [systemMessage.systemMessageTypeID])

    return (
        <div>
            {/* Kept as one block, widest scope first: the whole form, then the type control, then the dates. */}
            <NotificationInline
                show={disabled}
                text="This message is not active, so it can no longer be edited. Raise a new message instead"
                intent="warning">
            </NotificationInline>
            <NotificationInline
                id="notification-system-message-type"
                show={isUpdatingSystemMessage && !disabled}
                text="The message type is set when the message is created and cannot be changed afterwards"
                intent="warning">
            </NotificationInline>
            <NotificationInline
                id="notification-system-messages"
                loading={loading}
                show={systemMessage.systemMessageTypeID !== noSystemMessageTypeId && !disabled}
                text={toolTipText}
                intent="info">
            </NotificationInline>
            <FormSelect
                id="system-message-message-type"
                loading={loading}
                disabled={disabled || saving || isUpdatingSystemMessage}
                items={systemMessageTypes}
                placeholder="Select a message type"
                headingText="Message Type:"
                selectedValue={systemMessage.systemMessageTypeID}
                onItemSelect={onMessageTypeChange}
                dangerHelperText={errors["systemMessageTypeID"]}
            ></FormSelect>
            <ShowHide
                evaluator={systemMessageIdsWithNoDateField.includes(systemMessage.systemMessageTypeID)}
                hide={(
                    <>
                        <FormDateInput
                            id="system-message-start-date"
                            loading={loading}
                            disabled={disabled || saving || isStartDateLocked()}
                            headingText="Start Date:"
                            onChange={onStartDateChange}
                            value={systemMessage.startDate}
                            dangerHelperText={errors["startDate"]}
                            includeTime={true}
                            minDate={minStartDate}
                            maxDate={getMaxStartDate()}
                        ></FormDateInput>

                        <FormDateInput
                            id="system-message-end-date"
                            loading={loading}
                            disabled={disabled || saving}
                            headingText="End Date:"
                            onChange={onEndDateChange}
                            value={systemMessage.endDate}
                            dangerHelperText={errors["endDate"]}
                            includeTime={true}
                            minDate={getMinEndDate()}
                            maxDate={getMaxEndDate()}
                            defaultToNowWhenEmpty={isIncident}
                        ></FormDateInput>
                    </>
                )}
            ></ShowHide>
            <FormTextArea
                id="system-message-message"
                maxLength={1000}
                loading={loading}
                disabled={disabled || saving}
                headingText="Message:"
                value={systemMessage.message}
                onChange={onMessageChange}
                dangerHelperText={errors["message"]}
            ></FormTextArea>
            <FormTextInput
                id="system-message-url"
                loading={loading}
                disabled={disabled || saving}
                value={systemMessage.url}
                headingText="URL:"
                onChange={onUrlChange}
                dangerHelperText={errors["url"]}
            ></FormTextInput>
            <ShowHide
                evaluator={isActiveEditable && !isIncident}
                show={(
                    <FormSwitch
                        id="system-message-status"
                        loading={loading}
                        disabled={disabled || saving}
                        headingText="Active:"
                        checked={systemMessage.active}
                        onChange={onActiveChange}
                    ></FormSwitch>
                )}
            ></ShowHide>
        </div>
    );
}

SystemMessageForm.propTypes = {
    originalSystemMessage: PropTypes.object.isRequired,
    systemMessage: PropTypes.object.isRequired,
    systemMessageTypes: PropTypes.array.isRequired,
    onSystemMessageUpdate: PropTypes.func.isRequired,
    onValidationUpdate: PropTypes.func.isRequired,
    isSystemMessageDirty: PropTypes.bool,
    saving: PropTypes.bool,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    isActiveEditable: PropTypes.bool,
    isUpdatingSystemMessage: PropTypes.bool.isRequired
};